import React from 'react';
import * as LucideIcons from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';

const LevelingGuideModal = ({ onClose }) => {
    const { t } = useTranslation();

    const levelTiers = [
        { level: 1, name: t('levelingGuide.tiers.newStar'), features: { moons: 0, rings: 0, coreClass: 'bg-cyan-300' } },
        { level: 2, name: t('levelingGuide.tiers.theSpark'), features: { moons: 1, rings: 0, coreClass: 'bg-cyan-300' } },
        { level: 11, name: t('levelingGuide.tiers.protoStar'), features: { moons: 2, rings: 0, coreClass: 'bg-cyan-300' } },
        { level: 26, name: t('levelingGuide.tiers.ascendantStar'), features: { moons: 2, rings: 1, coreClass: 'bg-cyan-300' } },
        { level: 46, name: t('levelingGuide.tiers.radiantStar'), features: { moons: 2, rings: 2, coreClass: 'bg-cyan-300' } },
        { level: 66, name: t('levelingGuide.tiers.giantStar'), features: { moons: 2, rings: 2, coreClass: 'bg-amber-300', size: '1.25rem' } },
        { level: 86, name: t('levelingGuide.tiers.elderStar'), features: { moons: 2, rings: 2, coreClass: 'bg-yellow-200', size: '1.5rem', glow: true } }
    ];

    const VisualStar = ({ features }) => (
        <div className="relative w-20 h-20 flex items-center justify-center flex-shrink-0">
            {features.rings > 0 && <div className="absolute border-2 border-indigo-400/50 rounded-full w-14 h-14 animate-spin-slow"></div>}
            {features.rings > 1 && <div className="absolute border-2 border-pink-400/50 rounded-full w-20 h-20 animate-spin-reverse-slow"></div>}
            {features.moons > 0 && <div className="absolute w-full h-full animate-orbit-1"><div className="absolute top-0 left-1/2 -ml-1 w-2 h-2 bg-gray-300 rounded-full"></div></div>}
            {features.moons > 1 && <div className="absolute w-full h-full animate-orbit-2"><div className="absolute bottom-0 left-1/2 -ml-1 w-2 h-2 bg-gray-300 rounded-full"></div></div>}
            <div
                className={`relative rounded-full ${features.coreClass}`}
                style={{
                    width: features.size || '1rem',
                    height: features.size || '1rem',
                    boxShadow: features.glow ? `0 0 15px ${features.coreClass}` : 'none'
                }}
            ></div>
        </div>
    );

    return (
        <div className="modal-overlay">
            <div className="bg-gray-900/80 backdrop-blur-md p-6 rounded-lg shadow-glow max-w-2xl w-full text-white relative border border-cyan-500/50 flex flex-col max-h-[90vh]">
                <div className="flex-shrink-0">
                    <h3 className="text-2xl font-bold mb-4 text-cyan-300 font-playfair text-center">{t('levelingGuide.title')}</h3>
                    <div className="mb-6 p-4 bg-black/20 rounded-lg">
                        <h4 className="font-bold text-lg text-white mb-2">{t('levelingGuide.howToEarn')}</h4>
                        <ul className="list-disc list-inside space-y-2 text-gray-300">
                            <li><Trans i18nKey="levelingGuide.feature1"><span className="font-bold text-cyan-400">Maintain Connections:</span> Gain passive XP daily for each user in your constellation.</Trans></li>
                            <li><Trans i18nKey="levelingGuide.feature2"><span className="font-bold text-yellow-400">Send Starlight Surges:</span> Invest in another user by sending them a Surge. For the next 24 hours, you'll receive a 10% bonus from all Echoes they earn through amplifications.</Trans></li>
                            <li><Trans i18nKey="levelingGuide.feature3"><span className="font-bold text-indigo-400">Chart a Course:</span> Successfully completing a 24-hour collaborative quest with a partner yields a massive XP reward.</Trans></li>
                            <li><Trans i18nKey="levelingGuide.feature4"><span className="font-bold text-purple-400">Strengthen Links:</span> Spending Echoes to permanently upgrade a connection link provides a significant XP boost.</Trans></li>
                        </ul>
                    </div>
                </div>
                <div className="flex-grow overflow-y-auto custom-scrollbar pr-2 space-y-4">
                    {levelTiers.map(tier => (
                        <div key={tier.level} className="flex items-center p-3 bg-gray-800/50 rounded-lg gap-4">
                            <VisualStar features={tier.features} />
                            <div className="flex-grow">
                                <p className="font-bold text-white text-lg">{tier.name}</p>
                                <p className="text-sm text-cyan-300">{t('levelingGuide.unlocksAt', { level: tier.level })}</p>
                            </div>
                        </div>
                    ))}
                </div>
                <button onClick={onClose} className="absolute top-3 right-3 p-2 rounded-full bg-gray-700/50 hover:bg-gray-600/50 transition duration-300" aria-label={t('levelingGuide.close')}><LucideIcons.X size={20} /></button>
            </div>
        </div>
    );
};

export default LevelingGuideModal;